/**
 * Walker operational home (#133).
 *
 * Today's assigned walks — start/complete with timestamp + notes.
 * Walker owns `bookings.walking:read:self` and `bookings.walking:write:self`;
 * the server gates every endpoint with those scopes, so no tenantId
 * injection is needed from the client.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import { useAuth } from '../contexts/AuthContext.js';
import {
  walkerApi,
  todayIsoDate,
  type WalkAssignment,
  type WalkAssignmentsResponse,
  humanizeApiError,
} from '../lib/api.js';
import { TimeClockCard, type TimeClockStatus } from '../components/timeclock/TimeClockCard.js';
import { WelcomeCard } from '../components/WelcomeCard.js';

type WalkerHomeProps = { config: ClientConfig };

function statusLabel(status: WalkAssignment['status']): string {
  const map: Record<WalkAssignment['status'], string> = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    checked_in: 'In Progress',
    checked_out: 'Completed',
    cancelled: 'Cancelled',
  };
  return map[status] ?? status;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function WalkerHome({ config }: WalkerHomeProps): JSX.Element {
  const { staff } = useAuth();
  const apiUrl = config.deployment.apiUrl;
  const [date, setDate] = useState(todayIsoDate);
  const [data, setData] = useState<WalkAssignmentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeNotes, setActiveNotes] = useState<Record<string, string>>({});
  // #427 W1a — punch status lifted from the Time Clock card (spec W1a /
  // P2): clocked-out ⇒ Clock In is the view's sole filled-primary and
  // the walk Start/Complete actions demote to secondary.
  const [clockStatus, setClockStatus] = useState<TimeClockStatus>('unknown');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const res = await walkerApi.getAssignments(apiUrl, date);
    setIsLoading(false);
    if (res.error) setError(humanizeApiError(res.error));
    else if (res.data) setData(res.data);
  }, [apiUrl, date]);

  useEffect(() => { void load(); }, [load]);

  const handleStart = async (a: WalkAssignment): Promise<void> => {
    setPendingId(a.id);
    setActionError(null);
    const res = await walkerApi.startWalk(apiUrl, a.id, activeNotes[a.id]);
    setPendingId(null);
    if (res.error) { setActionError(res.error); return; }
    if (res.data) {
      setData((prev) =>
        prev
          ? {
              ...prev,
              assignments: prev.assignments.map((x) =>
                x.id === a.id ? res.data!.assignment : x,
              ),
            }
          : prev,
      );
    }
  };

  const handleComplete = async (a: WalkAssignment): Promise<void> => {
    const notes = activeNotes[a.id] ?? '';
    if (!notes.trim()) {
      setActionError('Please add completion notes before marking done.');
      return;
    }
    setPendingId(a.id);
    setActionError(null);
    const res = await walkerApi.completeWalk(apiUrl, a.id, notes);
    setPendingId(null);
    if (res.error) { setActionError(res.error); return; }
    if (res.data) {
      setData((prev) =>
        prev
          ? {
              ...prev,
              assignments: prev.assignments.map((x) =>
                x.id === a.id ? res.data!.assignment : x,
              ),
            }
          : prev,
      );
      setActiveNotes((prev) => {
        const next = { ...prev };
        delete next[a.id];
        return next;
      });
    }
  };

  const prev = (): void => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    setDate(d.toISOString().slice(0, 10));
  };
  const next = (): void => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    setDate(d.toISOString().slice(0, 10));
  };

  const assignments = data?.assignments ?? [];
  const pendingCount = assignments.filter((a) => a.status !== 'checked_out' && a.status !== 'cancelled').length;
  const completedCount = assignments.filter((a) => a.status === 'checked_out').length;

  // #427 W1a view-level primary hierarchy (P2 — one filled-primary):
  // while clocked out the card owns the primary and the walk actions
  // render via the deprioritized modifier; once clocked in the queue
  // resumes primary and the card (primaryDemoted) renders secondary.
  const queuePrimaryClass =
    clockStatus === 'clocked_out'
      ? 'ferro-btn ferro-btn--deprioritized ferro-btn--sm'
      : 'ferro-btn ferro-btn--primary ferro-btn--sm';

  return (
    <section className="ferro-admin-page ferro-role-screen">
      <PageHeader
        title={`My Walks${staff?.first_name ? ` · ${staff.first_name}` : ''}`}
        subtitle={`${assignments.length} walk${assignments.length !== 1 ? 's' : ''} assigned`}
      />

      {/* #431 — first login only: the walker blurb from their invite
          email, echoed on the screen the email pointed at. */}
      <WelcomeCard config={config} />

      {/* #427 W1a — punch in from the phone right on arrival (story S1,
          P7 hands-first). */}
      <TimeClockCard
        config={config}
        primaryDemoted={clockStatus !== 'clocked_out'}
        onStatusChange={setClockStatus}
      />

      {/* Date navigation */}
      <div className="ferro-date-nav">
        <button type="button" className="ferro-btn ferro-btn--outline ferro-btn--sm" onClick={prev} aria-label="Previous day">‹</button>
        <span className="ferro-date-nav__label">{new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
        <button type="button" className="ferro-btn ferro-btn--outline ferro-btn--sm" onClick={next} aria-label="Next day">›</button>
        <button type="button" className="ferro-btn ferro-btn--ghost ferro-btn--sm" onClick={() => setDate(todayIsoDate())} aria-label="Go to today">Today</button>
      </div>

      {/* Summary */}
      {!isLoading && !error && (
        <div className="ferro-summary-strip">
          <span className="ferro-summary-chip">{pendingCount} remaining</span>
          <span className="ferro-summary-chip ferro-summary-chip--accent">{completedCount} completed</span>
        </div>
      )}

      {actionError && (
        <div className="ferro-alert ferro-alert--error ferro-alert--dismissible" role="alert">
          <span>{actionError}</span>
          <button type="button" className="ferro-alert__dismiss" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {isLoading && (
        <div className="ferro-skeleton-stack" data-testid="walker-skeleton">
          {[1, 2, 3].map((i) => <div key={i} className="ferro-skeleton ferro-skeleton--stat" />)}
        </div>
      )}

      {error && !isLoading && (
        <div className="ferro-alert ferro-alert--error" role="alert">
          {error}
          <button type="button" className="ferro-btn ferro-btn--sm ferro-btn--outline" style={{ marginLeft: '12px' }} onClick={() => void load()}>Retry</button>
        </div>
      )}

      {!isLoading && !error && assignments.length === 0 && (
        <div className="ferro-empty-card" data-testid="walker-empty">
          <div className="ferro-empty-card__icon">🐾</div>
          <p>No walks assigned for this day.</p>
        </div>
      )}

      <div className="ferro-roster" data-testid="walker-assignments">
        {assignments.map((a) => {
          const isInProgress = a.status === 'checked_in';
          const isDone = a.status === 'checked_out';
          const isCancelled = a.status === 'cancelled';
          return (
            <div
              key={a.id}
              className={`ferro-roster-card${isInProgress ? ' ferro-roster-card--inprogress' : ''}${isDone ? ' ferro-roster-card--completed' : ''}`}
              data-testid={`walk-card-${a.id}`}
            >
              <div className="ferro-roster-card__header">
                <div>
                  <h3 className="ferro-roster-card__name">{a.dog.name}</h3>
                  <p className="ferro-roster-card__meta">{a.dog.breed ?? 'Dog'} · {a.customer.firstName} {a.customer.lastName}</p>
                </div>
                <span className={`ferro-walk-status ferro-walk-status--${a.status}`}>
                  {statusLabel(a.status)}
                </span>
              </div>

              {a.startTime && (
                <p className="ferro-roster-card__meta">Scheduled: {formatTime(a.startTime)}</p>
              )}
              {a.startedAt && (
                <p className="ferro-roster-card__meta">Started: {formatTime(a.startedAt)}</p>
              )}
              {a.completedAt && (
                <p className="ferro-roster-card__meta">Completed: {formatTime(a.completedAt)}</p>
              )}

              {/* Notes textarea — shown when not done/cancelled */}
              {!isDone && !isCancelled && (
                <textarea
                  className="ferro-notes-area"
                  placeholder={isInProgress ? 'Add completion notes (required)…' : 'Pre-walk notes…'}
                  value={activeNotes[a.id] ?? ''}
                  onChange={(e) =>
                    setActiveNotes((prev) => ({ ...prev, [a.id]: e.target.value }))
                  }
                  aria-label={`Notes for ${a.dog.name}`}
                />
              )}

              {a.walkerNotes && isDone && (
                <p className="ferro-roster-card__meta" style={{ fontStyle: 'italic' }}>{a.walkerNotes}</p>
              )}

              {!isCancelled && (
                <div className="ferro-roster-card__actions">
                  {a.status === 'pending' || a.status === 'confirmed' ? (
                    <button
                      type="button"
                      className={queuePrimaryClass}
                      disabled={pendingId === a.id}
                      onClick={() => void handleStart(a)}
                    >
                      {pendingId === a.id ? '…' : 'Start Walk'}
                    </button>
                  ) : null}
                  {isInProgress ? (
                    <button
                      type="button"
                      className={queuePrimaryClass}
                      disabled={pendingId === a.id}
                      onClick={() => void handleComplete(a)}
                    >
                      {pendingId === a.id ? '…' : 'Mark Complete'}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
